/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Input as AntInput } from 'antd'
import React from 'react'
import { paragraphSmall, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'

type TextAreaInputProps = {
  name: string
  value: string
  setValue: (value: string) => void
  fieldErrors: any
  customError: any
  label: string
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  onBlur?: () => void
  rows?: number
  showCharacterCount?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Optional autoSize for Ant TextArea; can be boolean or { minRows, maxRows } */
  autoSize?: boolean | { minRows?: number; maxRows?: number }
}

const { TextArea } = AntInput

export const TextAreaInput: React.FC<TextAreaInputProps> = ({
  name,
  value,
  setValue,
  fieldErrors,
  customError,
  label,
  placeholder,
  maxLength = 255,
  disabled = false,
  onBlur,
  rows = 4,
  showCharacterCount = true,
  tabIndex,
  autoSize,
}) => {
  const [characterCount, setCharacterCount] = React.useState(0)

  React.useEffect(() => {
    setCharacterCount(value?.length || 0)
  }, [value])

  return (
    <>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        `}
      >
        <p
          css={css`
            ${paragraphSmall}
            ${weightRegular}
            color: ${colors.WHITE};
            align-self: stretch;
            overflow-wrap: break-word;
          `}
        >
          {label}
        </p>
        <p
          css={css`
            ${paragraphSmall}
            ${weightRegular}
                color: ${colors.LIGHTER_GRAY};
            align-self: stretch;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            ${!showCharacterCount && 'display: none;'}
          `}
        >
          {characterCount}/{maxLength}
        </p>
      </div>
      <TextArea
        tabIndex={tabIndex}
        name={name}
        status={
          fieldErrors[name] ||
          (Array.isArray(fieldErrors) && fieldErrors?.includes(name))
            ? 'error'
            : ''
        }
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={maxLength}
        rows={autoSize ? undefined : rows}
        autoSize={autoSize}
        placeholder={placeholder}
        css={css`
          border-radius: 8px;
          border: 1px solid ${colors.SECONDARY_100};
          background: ${colors.SECONDARY_200};
          ${disabled &&
          css`
            color: ${colors.NEUTRAL_100};
          `}
          ${!disabled &&
          css`
            color: ${colors.WHITE};
          `}
        `}
        onBlur={onBlur}
        disabled={disabled}
      />
      <ErrorLabel
        fieldErrors={fieldErrors}
        customError={customError}
        name={name}
      />
    </>
  )
}
